import React, { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, Link } from 'react-router-dom';
import Icon from './Icon';
import NavDrawer from './NavDrawer';
import BackToTop from './BackToTop';
import {
  fetchAgenda,
  fetchAttendees,
  fetchSupplierAttendees,
  fetchSponsors,
  fetchFaqs,
  fetchGuests,
  fetchPhotos,
  fetchResources,
  fetchLocal,
  fetchUpcomingMeetings,
  useLive,
} from '../data/live';

// Nav order/labels per the redesign spec. `count` names which live count to show.
const NAV = [
  { to: 'overview', n: '01', name: 'Overview' },
  { to: 'agenda', n: '02', name: 'Agenda', count: 'agenda' },
  { to: 'attendees', n: '03', name: 'Attendees', count: 'attendees' },
  { to: 'sponsors', n: '04', name: 'Sponsors', count: 'sponsors' },
  { to: 'hotel', n: '05', name: 'Hotel' },
  { to: 'gallery', n: '06', name: 'Photos' },
  { to: 'resources', n: '07', name: 'Resources' },
  { to: 'faq', n: '08', name: 'FAQ', count: 'faq' },
  { to: 'local-guide', n: '09', name: 'Local Guide' },
  { to: 'upcoming', n: '10', name: 'Upcoming Meetings' },
];

export default function AppShell({ meeting }) {
  const eventId = meeting._event?.id;
  const seriesId = meeting._event?.series_id;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef(null);
  // Restore focus to the hamburger when the drawer closes (a11y).
  const closeDrawer = () => {
    setDrawerOpen(false);
    if (hamburgerRef.current) hamburgerRef.current.focus();
  };

  // Sidebar counts — same fetchers the pages use; null until resolved (or absent).
  const agenda = useLive(fetchAgenda, [eventId]);
  const providers = useLive(fetchAttendees, [eventId]);
  const suppliers = useLive(fetchSupplierAttendees, [eventId]);
  const sponsors = useLive(fetchSponsors, [eventId]);
  const faqs = useLive(fetchFaqs, [eventId, seriesId]);

  const counts = {
    agenda: agenda ? Object.values(agenda).reduce((n, arr) => n + (arr ? arr.length : 0), 0) : null,
    attendees: (providers || suppliers)
      ? (providers?.length || 0)
        + (suppliers?.corporateMembers?.length || 0)
        + (suppliers?.sponsors?.length || 0)
      : null,
    sponsors: sponsors ? sponsors.length : null,
    faq: faqs ? faqs.length : null,
  };
  const navItems = NAV.map((it) => ({ ...it, count: it.count ? counts[it.count] : undefined }));

  // Warm the remaining pages in the background so every tab opens instantly
  // and is saved for offline use. Deferred so it never competes with the
  // page the attendee is looking at.
  useEffect(() => {
    if (!eventId) return undefined;
    const id = setTimeout(() => {
      fetchGuests(eventId);
      fetchPhotos(eventId);
      fetchResources(eventId);
      fetchLocal(eventId);
      fetchUpcomingMeetings(eventId);
    }, 1500);
    return () => clearTimeout(id);
  }, [eventId]);

  // Countdown days (recomputed each minute like the old banner).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const days = Math.max(0, Math.floor((new Date(meeting.dateISO).getTime() - now) / 86400000));

  // Close the drawer when crossing up to desktop. (matchMedia is guarded for jsdom.)
  useEffect(() => {
    const mq = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(min-width: 900px)')
      : null;
    if (!mq) return undefined;
    const handler = (e) => { if (e.matches) setDrawerOpen(false); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <div className="portal-app">
      <header className="portal-appheader">
        <div className="portal-appheader-inner">
          <button
            type="button"
            className="portal-hamburger"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
            ref={hamburgerRef}
          >
            <Icon name="menu" size={22} />
          </button>

          <Link to={`/${meeting.slug}/overview`} className="portal-header-logo" aria-label={`${meeting.org} — Overview`}>
            <img src={meeting.logo} alt={`${meeting.org} logo`} />
          </Link>

          <span className="portal-header-divider" aria-hidden="true" />

          <div className="portal-header-meta">
            <div className="portal-header-title">{meeting.title}</div>
            <div className="portal-header-venue">{meeting.venue} · {meeting.city}</div>
          </div>

          <div className="portal-header-right">
            <span className="portal-session-chip">{meeting.org} 2026</span>
            <Link to="/" className="portal-exit-btn">Exit</Link>
          </div>
        </div>
      </header>

      <NavDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        meeting={meeting}
        navItems={navItems}
        days={days}
      />

      <div className="portal-appgrid">
        <aside className="portal-sidebar">
          <div className="portal-sidebar-eyebrow">This Meeting</div>
          <nav className="portal-sidebar-nav">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) => `portal-nav-item${isActive ? ' active' : ''}`}
              >
                <span className="portal-nav-index">{it.n}</span>
                <span className="portal-nav-label">{it.name}</span>
                {it.count != null && <span className="portal-nav-count">{it.count}</span>}
              </NavLink>
            ))}
          </nav>

          <div className="portal-countdown-card">
            <div className="portal-countdown-eyebrow">Countdown</div>
            <div className="portal-countdown-days">{days}</div>
            <div className="portal-countdown-note">days until the opening session</div>
          </div>
        </aside>

        <main className="portal-main">
          <div className="portal-main-inner">
            <Outlet context={{ meeting }} />
          </div>
        </main>
      </div>

      <footer className="portal-appfooter">
        <div className="portal-appfooter-inner">
          <span className="portal-footer-mono">GoIHES · Attendee Portal · 2026</span>
          <span className="portal-footer-links">
            <Link to="/">Choose another meeting</Link>
          </span>
        </div>
      </footer>

      <BackToTop />
    </div>
  );
}
